"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, Sparkles } from "lucide-react";
const milestones = [{
  id: "m1",
  year: "2013",
  title: "The Apple Standard",
  description: "TidalSwing AI established its core as an Apple-focused innovator, blending aesthetics with enterprise-grade mobile architecture. We focused on delivering high-performance digital solutions native to the Apple ecosystem.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/technova_genesis_abstract_core.png"
}, {
  id: "m2",
  year: "2019",
  title: "Salesforce Strategic Partner",
  description: "After several years as a Salesforce Customer, we initiated partnership with Salesforce to build scalable SaaS ecosystems, becoming a trusted SI, ISV, and OEM partner. We integrate complex cloud architectures with visionary product development for global B2B, B2C, and B2G environments.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/technova_cyber_shield_neon.png"
}, {
  id: "m3",
  year: "2025",
  title: "OceanClaw Orchestrator",
  description: "Launched the development of OceanClaw, a platform bridging AI computing infrastructure with secure, high-performance crypto gateways. This marked our shift into building sovereign digital ecosystems for the future of compute.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/technova_glass_ui_abstract.png"
}, {
  id: "m4",
  year: "2026",
  title: "OceanWallet Blockchain",
  description: "Finalized the OceanWallet ecosystem, enabling stablecoin rewards for secure network processing. Our current focus empowers users to apply rewards toward personal agent workflows or immediate USDC liquidity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/technova_future_grid_purple.png"
}];
function TimelineItem({
  item,
  index
}: {
  item: typeof milestones[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: false,
    threshold: 0.3
  });
  return <div data-section-id="105043" ref={ref} data-index={index} className={`relative pl-8 md:pl-16 py-16 md:py-32 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className={`absolute left-[-5px] top-24 md:top-40 size-3 rounded-full border-2 border-background transition-colors duration-700 ${isVisible ? "bg-primary shadow-[0_0_15px_var(--color-primary)]" : "bg-muted"}`} />

      <div className="flex flex-col gap-6">
        <div className="flex items-baseline gap-4">
          <span className="text-5xl md:text-7xl font-mono font-bold text-muted/20 tracking-tighter">
            {item.year}
          </span>
          <h3 className="text-2xl md:text-4xl font-default font-semibold tracking-tight text-foreground">
            {item.title}
          </h3>
        </div>

        <p className="text-lg font-default text-muted-foreground leading-relaxed max-w-2xl">
          {item.description}
        </p>

        <div className="mt-8 relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 p-2 backdrop-blur-md shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-secondary/10 opacity-50 mix-blend-overlay pointer-events-none" />
          <AspectRatio ratio={16 / 9} className="rounded-xl overflow-hidden bg-muted/20">
            <img src={item.image} alt={item.title} className={`object-cover w-full h-full transition-transform duration-1000 ${isVisible ? "scale-100" : "scale-105"}`} />
          </AspectRatio>
        </div>
      </div>
    </div>;
}
export default function InnovationPhilosophy() {
  return <section id="the-ecosystem-builders" className="dark bg-background text-foreground relative w-full overflow-hidden selection:bg-primary/30 selection:text-primary-foreground">
      {}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-secondary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 relative">
          
          {}
          <div className="lg:w-2/5 relative z-10">
            <div className="lg:sticky lg:top-32 flex flex-col gap-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/10 text-primary w-fit text-sm font-default font-medium">
                <Sparkles className="size-4" />
                <span>Control Pane</span>
              </div>
              
              <h2 className="text-6xl md:text-8xl font-default font-bold tracking-tighter uppercase leading-[0.85]">
                <span className="block text-transparent bg-clip-text bg-gradient-to-b from-foreground to-foreground/50">
                  THE
                </span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-pulse">
                  BUILDERS
                </span>
              </h2>
              
              <p className="text-xl font-default text-muted-foreground leading-relaxed max-w-md">We don't just consult; we architect. Explore the milestones that define TidalSwing AI's commitment to building scalable, sovereign digital ecosystems.</p>

              <div className="pt-8">
                <Link to="#">
                  <Button size="lg" className="rounded-sm font-default group bg-primary text-primary-foreground hover:bg-primary/90">
                    Explore Our Systems
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:w-3/5 relative">
            {}
            <Separator orientation="vertical" className="absolute left-0 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-border to-transparent" />

            <div className="flex flex-col">
              {milestones.map((item, index) => <TimelineItem key={item.id} item={item} index={index} />)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}